import React from "react";
import { Form } from "react-bootstrap";
import DatePicker from "react-datepicker";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import moment from "moment";
import {
  CARD_BORDER,
  DEBT_CARD_TITLE_FONT_SIZE,
  DEBT_DRAWER_MAX_WIDTH,
  MECHANISM_FULL,
  MECHANISM_PERCENT,
  NAVY,
} from "../debt-collection-section/DebtCollectionSectionConstants";
import { formatMoney } from "../debt-collection-section/DebtCollectionSectionHelpers";
import { InfoPanel } from "../debt-collection-section/DebtCollectionSectionUi";

/**
 * Right off-canvas form to create or edit a franchise debt configuration.
 * @param {object} props - Drawer props.
 * @param {boolean} props.open - Whether the panel is shown.
 * @param {"create"|"edit"} props.drawerMode - Create vs edit behaviour.
 * @param {boolean} props.submitting - Disables interactions while saving.
 * @param {object} props.form - Controlled form state object.
 * @param {function} props.setForm - Form state setter.
 * @param {object} props.formErrors - Field-level validation errors.
 * @param {function} props.setFormErrors - Form errors setter.
 * @param {function} props.onClose - Closes the drawer.
 * @param {function} props.onSubmit - Submits the form (create/update).
 * @param {function} props.dismissFieldErrorIfValid - Clears a field error when input becomes valid.
 * @returns {JSX.Element|null} Off-canvas markup or null when closed.
 */
export function DebtCollectionSectionDrawer({
  open,
  drawerMode,
  submitting,
  form,
  setForm,
  formErrors,
  setFormErrors,
  onClose,
  onSubmit,
  dismissFieldErrorIfValid,
}) {
  if (!open) return null;

  const mechanismCardsDisabledStyle = submitting
    ? {
        opacity: 0.55,
        cursor: "not-allowed",
        pointerEvents: "none",
      }
    : {};

  return (
    <>
      <div
        id="kt_franchise_debt_panel"
        className="offcanvas offcanvas-right offcanvas-on d-flex flex-column pt-0"
        style={{
          zIndex: 1050,
          width: "100%",
          maxWidth: DEBT_DRAWER_MAX_WIDTH,
        }}
      >
        <form
          className="d-flex flex-column debt-drawer-form"
          onSubmit={onSubmit}
        >
          <div
            className="offcanvas-header flex-shrink-0 d-flex align-items-center justify-content-between border-bottom px-10 py-3 mb-0 bg-white"
            style={{ borderColor: CARD_BORDER }}
          >
            <h4
              className="font-weight-bolder mb-0 pr-3"
              style={{ color: NAVY, fontSize: DEBT_CARD_TITLE_FONT_SIZE }}
            >
              {drawerMode === "edit"
                ? "Edit configuration"
                : "Create configuration"}
            </h4>
            <button
              type="button"
              className="btn btn-xs btn-icon btn-light btn-hover-primary flex-shrink-0 d-flex align-items-center justify-content-center debt-drawer-close-btn"
              onClick={() => !submitting && onClose()}
              aria-label="Close"
              style={{
                width: 36,
                height: 36,
                padding: 0,
                borderRadius: 6,
                borderColor: CARD_BORDER,
              }}
            >
              <i className="ki ki-close icon-sm text-muted" />
            </button>
          </div>

          <div
            className="px-10 pb-4 debt-drawer-scroll-body"
            style={{ paddingTop: "20px" }}
          >
            <div className="row align-items-stretch">
              <div className="col-lg-6 mb-4 mb-lg-0 pr-lg-3">
                <div className="debt-drawer-step-panel h-100 bg-white">
                  <h6
                    className="font-weight-bold mb-3"
                    style={{
                      color: NAVY,
                      fontSize: DEBT_CARD_TITLE_FONT_SIZE,
                    }}
                  >
                    1. Select mechanism
                  </h6>
                  <Form.Group className="mb-3">
                    <div
                      className="d-flex flex-row align-items-stretch debt-mechanism-cards"
                      style={{ gap: "12px" }}
                    >
                      <div
                        role="button"
                        tabIndex={submitting ? -1 : 0}
                        aria-disabled={submitting}
                        onKeyDown={(e) => {
                          if (submitting) return;
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            const nextForm = {
                              ...form,
                              collection_mechanism: MECHANISM_PERCENT,
                            };
                            setForm((f) => ({
                              ...f,
                              collection_mechanism: MECHANISM_PERCENT,
                            }));
                            dismissFieldErrorIfValid(
                              "collection_percent",
                              nextForm,
                            );
                          }
                        }}
                        onClick={() => {
                          if (submitting) return;
                          const nextForm = {
                            ...form,
                            collection_mechanism: MECHANISM_PERCENT,
                          };
                          setForm((f) => ({
                            ...f,
                            collection_mechanism: MECHANISM_PERCENT,
                          }));
                          dismissFieldErrorIfValid(
                            "collection_percent",
                            nextForm,
                          );
                        }}
                        className="card p-3 bg-white flex-fill"
                        style={{
                          cursor: submitting ? "not-allowed" : "pointer",
                          minWidth: 0,
                          border:
                            form.collection_mechanism === MECHANISM_PERCENT
                              ? `2px solid ${NAVY}`
                              : `1px solid ${CARD_BORDER}`,
                          ...mechanismCardsDisabledStyle,
                        }}
                      >
                        <strong>% based collection</strong>
                        <small className="text-muted d-block mt-1">
                          Collect a fixed percentage per transaction
                        </small>
                      </div>
                      <div
                        role="button"
                        tabIndex={submitting ? -1 : 0}
                        aria-disabled={submitting}
                        onKeyDown={(e) => {
                          if (submitting) return;
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            setForm((f) => ({
                              ...f,
                              collection_mechanism: MECHANISM_FULL,
                            }));
                            setFormErrors((prev) => {
                              const next = { ...prev };
                              delete next.collection_percent;
                              return next;
                            });
                          }
                        }}
                        onClick={() => {
                          if (submitting) return;
                          setForm((f) => ({
                            ...f,
                            collection_mechanism: MECHANISM_FULL,
                          }));
                          setFormErrors((prev) => {
                            const next = { ...prev };
                            delete next.collection_percent;
                            return next;
                          });
                        }}
                        className="card p-3 bg-white flex-fill"
                        style={{
                          cursor: submitting ? "not-allowed" : "pointer",
                          minWidth: 0,
                          border:
                            form.collection_mechanism === MECHANISM_FULL
                              ? `2px solid ${NAVY}`
                              : `1px solid ${CARD_BORDER}`,
                          ...mechanismCardsDisabledStyle,
                        }}
                      >
                        <strong>100% collection</strong>
                        <small className="text-muted d-block mt-1">
                          Collect the full amount on each transaction
                        </small>
                      </div>
                    </div>
                  </Form.Group>

                  <div className="row form-row mx-n2 align-items-start">
                    {form.collection_mechanism === MECHANISM_PERCENT && (
                      <div className="col-md-6 px-2 mb-3 mb-md-0">
                        <Form.Group className="mb-0">
                          <Form.Label>% per transaction *</Form.Label>
                          <Form.Control
                            type="number"
                            min={1}
                            max={100}
                            value={form.collection_percent}
                            disabled={submitting}
                            onChange={(e) => {
                              if (submitting) return;
                              const v = e.target ? e.target.value : "";
                              const nextForm = {
                                ...form,
                                collection_percent: v,
                              };
                              setForm((f) => ({
                                ...f,
                                collection_percent: v,
                              }));
                              dismissFieldErrorIfValid(
                                "collection_percent",
                                nextForm,
                              );
                            }}
                            isInvalid={!!formErrors.collection_percent}
                          />
                          <Form.Control.Feedback type="invalid">
                            {formErrors.collection_percent}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </div>
                    )}
                    <div
                      className={`px-2 ${
                        form.collection_mechanism === MECHANISM_PERCENT
                          ? "col-md-6"
                          : "col-12"
                      }`}
                    >
                      <Form.Group className="mb-0">
                        <Form.Label>Started at *</Form.Label>
                        <div className="position-relative debt-drawer-date-field w-100">
                          <DatePicker
                            selected={form.start_date}
                            onChange={(d) => {
                              const nextForm = { ...form, start_date: d };
                              setForm((f) => ({ ...f, start_date: d }));
                              dismissFieldErrorIfValid("start_date", nextForm);
                            }}
                            minDate={moment()
                              .startOf("day")
                              .toDate()}
                            shouldCloseOnSelect
                            className={`form-control debt-drawer-date-input${
                              formErrors.start_date ? " is-invalid" : ""
                            }`}
                            wrapperClassName="w-100"
                            dateFormat="dd/MM/yyyy"
                            placeholderText="DD/MM/YYYY"
                            popperPlacement="bottom"
                          />
                          <button
                            type="button"
                            className="debt-drawer-date-calendar-btn"
                            onClick={(e) => {
                              e.preventDefault();
                              const wrap =
                                e.currentTarget.previousElementSibling;
                              const input =
                                wrap &&
                                wrap.querySelector &&
                                wrap.querySelector("input");
                              if (input) {
                                input.focus();
                                input.click();
                              }
                            }}
                            aria-label="Open calendar"
                          >
                            <CalendarTodayIcon
                              fontSize="small"
                              className="text-muted"
                            />
                          </button>
                        </div>
                        {formErrors.start_date && (
                          <div className="text-danger small mt-1">
                            {formErrors.start_date}
                          </div>
                        )}
                      </Form.Group>
                    </div>
                  </div>

                  <Form.Group className="mb-0 mt-3">
                    <Form.Label>
                      {drawerMode === "create"
                        ? "Unique ref *"
                        : "Unique ref"}
                    </Form.Label>
                    <Form.Control
                      value={form.unique_reference}
                      readOnly={drawerMode === "edit"}
                      disabled={drawerMode === "edit"}
                      className={
                        drawerMode === "edit"
                          ? "bg-light text-muted"
                          : undefined
                      }
                      onChange={(e) => {
                        if (drawerMode === "edit") return;
                        const v = e.target ? e.target.value : "";
                        setForm((f) => ({ ...f, unique_reference: v }));
                        if (String(v).trim()) {
                          setFormErrors((prev) => {
                            if (!prev.unique_reference) return prev;
                            const next = { ...prev };
                            delete next.unique_reference;
                            return next;
                          });
                        }
                      }}
                      onBlur={() => {
                        if (drawerMode === "edit") return;
                        if (!String(form.unique_reference).trim()) {
                          setFormErrors((prev) => ({
                            ...prev,
                            unique_reference: "Unique reference is required.",
                          }));
                        } else {
                          setFormErrors((prev) => {
                            const next = { ...prev };
                            delete next.unique_reference;
                            return next;
                          });
                        }
                      }}
                      isInvalid={!!formErrors.unique_reference}
                    />
                    <Form.Control.Feedback type="invalid">
                      {formErrors.unique_reference}
                    </Form.Control.Feedback>
                  </Form.Group>
                </div>
              </div>

              <div className="col-lg-6 pl-lg-3">
                <div className="debt-drawer-step-panel h-100 bg-white">
                  <h6
                    className="font-weight-bold mb-3"
                    style={{
                      color: NAVY,
                      fontSize: DEBT_CARD_TITLE_FONT_SIZE,
                    }}
                  >
                    2. Balance setup
                  </h6>
                  <Form.Group className="mb-3">
                    <Form.Label>Opening balance (£) *</Form.Label>
                    <Form.Control
                      type="text"
                      inputMode="decimal"
                      value={form.opening_balance}
                      onChange={(e) => {
                        const v = e.target ? e.target.value : "";
                        const nextForm = { ...form, opening_balance: v };
                        setForm((f) => ({ ...f, opening_balance: v }));
                        dismissFieldErrorIfValid(
                          "opening_balance",
                          nextForm,
                        );
                      }}
                      isInvalid={!!formErrors.opening_balance}
                    />
                    <Form.Control.Feedback type="invalid">
                      {formErrors.opening_balance}
                    </Form.Control.Feedback>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Current balance (£)</Form.Label>
                    {drawerMode === "create" ? (
                      <Form.Control
                        readOnly
                        disabled
                        className="bg-light text-muted"
                        value={(() => {
                          const n = Number(
                            String(form.opening_balance).replace(/,/g, ""),
                          );
                          if (
                            form.opening_balance === "" ||
                            Number.isNaN(n)
                          ) {
                            return "";
                          }
                          return formatMoney(n);
                        })()}
                      />
                    ) : (
                      <>
                        <Form.Control
                          readOnly
                          disabled
                          className="bg-light font-weight-bold"
                          value={formatMoney(form.current_balance)}
                        />
                        <Form.Text className="text-muted">
                          System-controlled after creation.
                        </Form.Text>
                      </>
                    )}
                  </Form.Group>

                  <InfoPanel title="Business rules" className="mb-0">
                    <ul className="small text-dark pl-3 mb-0">
                      <li>Only super admin can create a configuration.</li>
                      <li>
                        Adding a new configuration is blocked if an existing configuration is Pending or Ongoing.
                      </li>
                      <li>
                        Current balance starts equal to opening balance.
                      </li>
                    </ul>
                  </InfoPanel>
                </div>
              </div>
            </div>
          </div>

          <div
            className="offcanvas-footer flex-shrink-0 px-10 border-top bg-white"
            style={{
              borderColor: CARD_BORDER,
              paddingTop: "15px",
              paddingBottom: "calc(50px + env(safe-area-inset-bottom, 0px))",
              width: "100%",
            }}
          >
            <div
              className="d-flex justify-content-end align-items-center flex-wrap"
              style={{ gap: "12px" }}
            >
              <button
                type="button"
                disabled={submitting}
                onClick={onClose}
                className="btn btn-dark font-weight-bolder font-size-sm"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="btn btn-primary font-weight-bolder font-size-sm"
              >
                {submitting
                  ? drawerMode === "create"
                    ? "Creating…"
                    : "Saving…"
                  : drawerMode === "create"
                  ? "Create"
                  : "Save"}
              </button>
            </div>
          </div>
        </form>
      </div>
      <div
        className="offcanvas-overlay"
        style={{ zIndex: 1040 }}
        onClick={() => !submitting && onClose()}
        role="presentation"
      />
    </>
  );
}
